import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CalendarDays,
  Users,
  ClipboardPaste,
  FileSpreadsheet,
  FileText,
  Printer,
  Check,
  Sparkles,
  Trash2,
  RefreshCw,
  Info,
  LogOut,
  ShieldCheck,
  ClipboardCheck,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Providers, Days, Census, Documentation, UNITS, downloadAuthed } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ProviderRoster from "@/components/ProviderRoster";
import DayPicker from "@/components/DayPicker";
import PasteCensus from "@/components/PasteCensus";
import RoundingBoard from "@/components/RoundingBoard";
import DailyRolesPanel from "@/components/DailyRolesPanel";
import MedicalRounderPanel from "@/components/MedicalRounderPanel";
import ContinuityDiff from "@/components/ContinuityDiff";
import PrintSheet from "@/components/PrintSheet";
import DailyQuote from "@/components/DailyQuote";
import ClinicalTipCard from "@/components/ClinicalTipCard";
import QualityCommand from "@/components/QualityCommand";
import CommitteesDialog from "@/components/CommitteesDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [providers, setProviders] = useState([]);
  const [days, setDays] = useState([]);
  const [currentDate, setCurrentDate] = useState(todayIso());
  const [day, setDay] = useState(null);
  const [compliance, setCompliance] = useState(null); // documentation-compliance grid
  const [availableIds, setAvailableIds] = useState(new Set());
  const [phpProviderId, setPhpProviderId] = useState("");
  const [medRounderId, setMedRounderId] = useState("");
  const [medRounderPhp, setMedRounderPhp] = useState(false);
  const [psychOnCallId, setPsychOnCallId] = useState("");
  const [medOnCallId, setMedOnCallId] = useState("");
  const [censusText, setCensusText] = useState("");
  const [seedFromDate, setSeedFromDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [committeesOpen, setCommitteesOpen] = useState(false);

  // Load providers + days list
  const refreshCore = async () => {
    const [ps, ds] = await Promise.all([Providers.list(), Days.list()]);
    // Global alphabetical sort — every downstream list, dropdown, and column derives from this array.
    ps.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    setProviders(ps);
    setDays(ds);
    return { ps, ds };
  };

  const loadDay = async (date) => {
    try {
      const d = await Days.get(date);
      setDay(d);
      const ids = new Set(d.available_providers.map((c) => c.provider_id));
      setAvailableIds(ids);
      const php = d.available_providers.find((c) => c.covers_php);
      setPhpProviderId(php ? php.provider_id : "");
      setMedRounderId(d.medical_rounder_id || "");
      setMedRounderPhp(!!d.medical_rounder_covers_php);
      setPsychOnCallId(d.psych_on_call_id || "");
      setMedOnCallId(d.medical_on_call_id || "");
    } catch (e) {
      // Expected 404 when navigating to a not-yet-saved day. Silently reset.
      setDay(null);
      setPhpProviderId("");
      setMedRounderId("");
      setMedRounderPhp(false);
      setPsychOnCallId("");
      setMedOnCallId("");
    }
  };

  const loadCompliance = async (date) => {
    try {
      const c = await Documentation.compliance(date);
      setCompliance(c);
    } catch {
      setCompliance(null);
    }
  };

  useEffect(() => {
    (async () => {
      await refreshCore();
    })();
  }, []);

  useEffect(() => {
    loadDay(currentDate);
    loadCompliance(currentDate);
  }, [currentDate]);

  // If no day exists yet, prefill availableIds with all active psych_rounder providers
  useEffect(() => {
    if (!day && providers.length && availableIds.size === 0) {
      setAvailableIds(
        new Set(
          providers
            .filter((p) => p.active && (p.roles || ["psych_rounder"]).includes("psych_rounder"))
            .map((p) => p.id)
        )
      );
    }
  }, [day, providers, availableIds.size]);

  const availableProvidersCfg = useMemo(() => {
    return Array.from(availableIds).map((pid) => ({
      provider_id: pid,
      covers_php: pid === phpProviderId,
    }));
  }, [availableIds, phpProviderId]);

  const previousDate = useMemo(() => {
    const earlier = days.map((d) => d.date).filter((d) => d < currentDate).sort();
    return earlier.length ? earlier[earlier.length - 1] : "";
  }, [days, currentDate]);

  const handleGenerate = async () => {
    if (availableIds.size === 0) {
      toast.error("Select at least one available provider.");
      return;
    }
    if (!censusText.trim() && !day) {
      toast.error("Paste the current admissions list.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        date: currentDate,
        available_providers: availableProvidersCfg,
        census_text: censusText,
        seed_from_date: seedFromDate || previousDate || null,
        medical_rounder_id: medRounderId || null,
        medical_rounder_covers_php: !!medRounderPhp,
        psych_on_call_id: psychOnCallId || null,
        medical_on_call_id: medOnCallId || null,
      };
      const d = await Days.generate(payload);
      setDay(d);
      setCensusText("");
      await refreshCore();
      toast.success(`Assignments generated for ${currentDate}`);
      if ((d.warnings || []).length) {
        d.warnings.forEach((w) => toast.warning(w));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate assignments.");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePhp = async (pid) => {
    const next = phpProviderId === pid ? "" : pid;
    setPhpProviderId(next);
    if (day) {
      try {
        const d = await Days.setPhp(currentDate, next || null);
        setDay(d);
      } catch {
        toast.error("Failed to update PHP cover.");
      }
    }
  };

  const updateDayRoles = async (patch) => {
    if (!day) return;
    try {
      const d = await Days.setRoles(currentDate, patch);
      setDay(d);
    } catch {
      toast.error("Failed to update role.");
    }
  };

  const handleSetMedRounder = async (pid) => {
    const clear = !pid;
    setMedRounderId(pid || "");
    if (clear) setMedRounderPhp(false);
    await updateDayRoles(
      clear
        ? { clear_medical_rounder: true }
        : { medical_rounder_id: pid }
    );
  };

  const handleToggleMedRounderPhp = async () => {
    const next = !medRounderPhp;
    setMedRounderPhp(next);
    await updateDayRoles({ medical_rounder_covers_php: next });
  };

  const handleSetPsychOnCall = async (pid) => {
    setPsychOnCallId(pid || "");
    await updateDayRoles(pid ? { psych_on_call_id: pid } : { clear_psych_on_call: true });
  };

  const handleSetMedOnCall = async (pid) => {
    setMedOnCallId(pid || "");
    await updateDayRoles(pid ? { medical_on_call_id: pid } : { clear_medical_on_call: true });
  };

  const handleToggleAvailable = (pid) => {
    const next = new Set(availableIds);
    if (next.has(pid)) {
      next.delete(pid);
      if (phpProviderId === pid) setPhpProviderId("");
    } else next.add(pid);
    setAvailableIds(next);
  };

  const handleReset = async () => {
    if (!day) return;
    if (!confirm(`Delete assignments for ${currentDate}? This cannot be undone.`)) return;
    try {
      await Days.remove(currentDate);
      setDay(null);
      await refreshCore();
      toast.success("Day cleared.");
    } catch (e) {
      toast.error("Failed to clear day.");
    }
  };

  const handleExport = async (kind) => {
    if (!day) return;
    const url = kind === "xlsx" ? Days.exportXlsx(currentDate) : Days.exportDocx(currentDate);
    const filename = `rounding_${currentDate}.${kind}`;
    try {
      await downloadAuthed(url, filename);
    } catch (e) {
      toast.error(`Failed to download ${kind.toUpperCase()}.`);
    }
  };

  const handleSaveDay = async () => {
    // Every edit already auto-saves (drag/drop, patient edit, roles, rounding time).
    // This button re-fetches the day from the server to *confirm* the on-screen
    // state matches persisted state, and gives the user an explicit "Saved" toast.
    if (!day) return;
    try {
      const fresh = await Days.get(currentDate);
      setDay(fresh);
      toast.success(`Saved · ${fresh.patients?.length ?? 0} patient(s) confirmed.`);
    } catch (e) {
      toast.error("Failed to confirm save. Please try again.");
    }
  };

  const handleResetAll = async () => {
    if (
      !confirm(
        "Reset everything?\n\nThis will delete ALL saved days and ALL providers so you can start fresh with today's provider list.\n\nThis cannot be undone."
      )
    )
      return;
    try {
      const res = await Days.reset("all");
      setDay(null);
      setAvailableIds(new Set());
      setPhpProviderId("");
      setMedRounderId("");
      setMedRounderPhp(false);
      setPsychOnCallId("");
      setMedOnCallId("");
      setCensusText("");
      setSeedFromDate("");
      setCurrentDate(todayIso());
      await refreshCore();
      toast.success(
        `Reset complete — cleared ${res.days_removed} day(s) and ${res.providers_removed} provider(s).`
      );
    } catch (e) {
      toast.error("Failed to reset data.");
    }
  };

  const handleClearHistory = async () => {
    // Wipe ALL saved days but keep the provider roster intact.
    // Useful when the user wants a clean history slate for a new pilot period
    // without losing the provider list they already built.
    if (
      !confirm(
        "Clear all saved days?\n\nThis deletes every previous day's rounding list and discharges. Providers are KEPT.\n\nThis cannot be undone."
      )
    )
      return;
    try {
      const res = await Days.reset("days");
      setDay(null);
      setCensusText("");
      setSeedFromDate("");
      setCurrentDate(todayIso());
      await refreshCore();
      toast.success(`Cleared ${res.days_removed} day(s). Providers kept.`);
    } catch (e) {
      toast.error("Failed to clear history.");
    }
  };

  const handleCopyForward = () => {
    // Set target date = today or tomorrow relative to currentDate
    const d = new Date(currentDate + "T00:00:00");
    d.setDate(d.getDate() + 1);
    const next = d.toISOString().slice(0, 10);
    setSeedFromDate(currentDate);
    setCurrentDate(next);
    toast.info(`Starting build for ${next} — seeded from ${currentDate}`);
  };

  const providerMap = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.id, p])),
    [providers]
  );

  const displayDate = useMemo(() => {
    try {
      return format(new Date(currentDate + "T00:00:00"), "EEEE, MMM d, yyyy");
    } catch {
      return currentDate;
    }
  }, [currentDate]);

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] font-sans">
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-white border-b border-[#e2e8f0] shadow-sm no-print"
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5" data-testid="app-logo">
            <svg
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-7 h-7 text-[#0284c7]"
              aria-hidden="true"
            >
              <path
                d="M16 28c6.627 0 12-5.373 12-12S22.627 4 16 4 4 9.373 4 16s5.373 12 12 12Z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 16c0-3.866 3.134-7 7-7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="16" cy="16" r="2.5" fill="currentColor" />
            </svg>
            <div className="flex flex-col leading-tight">
              <span
                className="font-heading font-bold text-xl tracking-tight text-[#0f172a]"
                data-testid="app-title"
              >
                nikorounds
              </span>
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#94a3b8] font-medium">
                Daily rounds · Quality command
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <DayPicker
              value={currentDate}
              onChange={setCurrentDate}
              days={days}
              data-testid="day-picker"
            />
            <Dialog open={rosterOpen} onOpenChange={setRosterOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
                  data-testid="manage-providers-button"
                >
                  <Users className="w-4 h-4 mr-2" /> Providers
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="provider-roster-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">Provider roster</DialogTitle>
                  <DialogDescription className="text-[#475569]">
                    Manage the pool of providers and their unit preferences. Selected providers become available each day.
                  </DialogDescription>
                </DialogHeader>
                <ProviderRoster
                  providers={providers}
                  onChange={async () => {
                    const { ps } = await refreshCore();
                    // Keep availableIds valid
                    setAvailableIds((prev) => {
                      const next = new Set();
                      ps.forEach((p) => {
                        if (prev.has(p.id) || (prev.size === 0 && p.active)) next.add(p.id);
                      });
                      return next;
                    });
                  }}
                />
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              className="border-[#e2e8f0] hover:bg-[#f0f9ff] hover:border-[#0284c7] text-[#0284c7]"
              onClick={() => setQualityOpen(true)}
              data-testid="open-quality-command"
            >
              <ShieldCheck className="w-4 h-4 mr-2" /> Quality Command
            </Button>
            <QualityCommand open={qualityOpen} onOpenChange={setQualityOpen} />

            <Button
              variant="outline"
              className="border-[#e2e8f0] hover:bg-[#f0f9ff] hover:border-[#0284c7] text-[#0284c7]"
              onClick={() => setCommitteesOpen(true)}
              data-testid="open-committees"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" /> Committees
            </Button>
            <CommitteesDialog open={committeesOpen} onOpenChange={setCommitteesOpen} />

            <Button asChild variant="outline" className="border-[#e2e8f0] hover:bg-[#f0f9ff] hover:border-[#0284c7] text-[#0284c7]">
              <Link to="/flash" data-testid="open-daily-flash">
                <Zap className="w-4 h-4 mr-2" /> Daily Flash
              </Link>
            </Button>

            <Button
              variant="outline"
              className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
              disabled={!day}
              onClick={() => handleExport("xlsx")}
              data-testid="export-xlsx-button"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              variant="outline"
              className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
              disabled={!day}
              onClick={() => handleExport("docx")}
              data-testid="export-docx-button"
            >
              <FileText className="w-4 h-4 mr-2" /> Word
            </Button>
            <Button
              variant="outline"
              className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
              disabled={!day}
              onClick={() => window.print()}
              data-testid="print-button"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>

            <Button
              variant="default"
              className="bg-[#0284c7] hover:bg-[#21473c] text-white"
              disabled={!day}
              onClick={handleSaveDay}
              data-testid="save-day-button"
              title="Reload the day from the server and confirm every change is saved"
            >
              <Check className="w-4 h-4 mr-2" /> Save
            </Button>

            <Button
              variant="outline"
              className="border-[#f6d6ce] text-[#b94a48] hover:bg-[#fbebe7] hover:text-[#b94a48]"
              onClick={handleClearHistory}
              data-testid="clear-history-button"
              title="Delete every saved day but keep the provider roster"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear history
            </Button>

            <Button
              variant="outline"
              className="border-[#f3d3cf] text-[#b94a48] hover:bg-[#fbebe7] hover:text-[#b94a48]"
              onClick={handleResetAll}
              data-testid="reset-all-button"
              title="Delete every saved day and provider to start fresh"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Reset all
            </Button>

            {user && (
              <div className="flex items-center gap-2 pl-2 ml-1 border-l border-[#e2e8f0]" data-testid="user-chip">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-8 h-8 rounded-full border border-[#e2e8f0]"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#0284c7] text-white text-xs flex items-center justify-center font-semibold">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="hidden md:block text-right leading-tight">
                  <div className="text-xs font-medium text-[#0f172a]" data-testid="user-name">{user.name || user.email}</div>
                  <div className="text-[10px] text-[#475569]">{user.email}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#475569] hover:bg-[#f1f5f9]"
                  onClick={async () => {
                    await logout();
                    window.location.href = "/login";
                  }}
                  data-testid="logout-button"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Print title (legacy — replaced by PrintSheet above) */}
      <div className="hidden">
        <span aria-hidden="true">{displayDate}</span>
      </div>

      {/* Polished print sheet — rendered only for @media print */}
      <div className="print-only-wrapper">
        <PrintSheet day={day} providers={providers} />
      </div>

      <main className="max-w-[1600px] mx-auto px-6 py-8 space-y-8">
        {/* Setup panel */}
        <section className="space-y-6 no-print" data-testid="setup-panel">
          <div>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
                  Rounding day
                </p>
                <h2 className="font-heading text-3xl font-semibold tracking-tight text-[#0f172a] mt-1">
                  {displayDate}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {day && (
                  <>
                    <Badge
                      variant="outline"
                      className="border-[#e2e8f0] bg-white text-[#0284c7] font-medium"
                      data-testid="day-status-badge"
                    >
                      Saved · {day.patients.length} patient{day.patients.length !== 1 ? "s" : ""}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#b94a48] hover:bg-[#fbebe7] hover:text-[#b94a48]"
                      onClick={handleReset}
                      data-testid="clear-day-button"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Clear day
                    </Button>
                  </>
                )}
              </div>
            </div>
            <DailyQuote
              dateIso={currentDate}
              offset={day?.quote_offset || 0}
              onShuffle={async (nextOffset) => {
                if (!day) return;
                const updated = await Days.setShuffle(currentDate, { quote_offset: nextOffset });
                setDay(updated);
              }}
            />
            <ClinicalTipCard
              dateIso={currentDate}
              offset={day?.tip_offset || 0}
              onShuffle={async (nextOffset) => {
                if (!day) return;
                const updated = await Days.setShuffle(currentDate, { tip_offset: nextOffset });
                setDay(updated);
              }}
            />
          </div>

          {/* Available psych rounders */}
          <div
            className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
            data-testid="available-providers-panel"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
                  1. Psych rounders on duty
                </p>
                <h3 className="font-heading text-lg font-medium mt-1">
                  Who is rounding on the census today?
                </h3>
              </div>
              {providers.length === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#0284c7] text-[#0284c7] hover:bg-[#0284c7] hover:text-white"
                  onClick={() => setRosterOpen(true)}
                  data-testid="add-first-provider-button"
                >
                  Add your first provider
                </Button>
              )}
            </div>

            {providers.length === 0 ? (
              <p className="text-sm text-[#475569]">
                No providers yet. Click <span className="font-medium">Providers</span> above to add
                a roster.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {providers
                  .filter((p) => (p.roles || ["psych_rounder"]).includes("psych_rounder"))
                  .map((p) => {
                  const on = availableIds.has(p.id);
                  return (
                    <div
                      key={p.id}
                      data-testid={`provider-chip-${p.id}`}
                      className={[
                        "p-4 rounded-lg border transition-colors",
                        on
                          ? "border-[#0284c7] bg-[#f6f9f8]"
                          : "border-[#e2e8f0] bg-white hover:bg-[#f9f9f5]",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <label className="flex items-start gap-3 cursor-pointer flex-1">
                          <Checkbox
                            checked={on}
                            onCheckedChange={() => handleToggleAvailable(p.id)}
                            className="mt-0.5 border-[#c4c4c0] data-[state=checked]:bg-[#0284c7] data-[state=checked]:border-[#0284c7]"
                            data-testid={`toggle-available-${p.id}`}
                          />
                          <div>
                            <div className="font-medium text-[#0f172a]">{p.name}</div>
                            <div className="text-xs text-[#475569] mt-0.5">
                              {p.unit_preferences.length
                                ? `Prefers ${p.unit_preferences.join(", ")}`
                                : "No unit preference"}
                              {" · "}Cap {p.max_patients}
                            </div>
                          </div>
                        </label>
                      </div>
                      {on && (
                        <button
                          type="button"
                          onClick={() => handleTogglePhp(p.id)}
                          data-testid={`toggle-php-${p.id}`}
                          className={[
                            "mt-3 w-full text-xs font-semibold tracking-wider uppercase rounded px-2 py-1.5 transition-colors border",
                            phpProviderId === p.id
                              ? "bg-[#d96c4e] text-white border-[#d96c4e]"
                              : "bg-white text-[#475569] border-[#e2e8f0] hover:border-[#d96c4e] hover:text-[#d96c4e]",
                          ].join(" ")}
                        >
                          {phpProviderId === p.id ? "Covering PHP today" : "Mark PHP cover"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Daily roles panel */}
          <DailyRolesPanel
            providers={providers}
            medRounderId={medRounderId}
            medRounderPhp={medRounderPhp}
            psychOnCallId={psychOnCallId}
            medOnCallId={medOnCallId}
            onSetMedRounder={handleSetMedRounder}
            onToggleMedRounderPhp={handleToggleMedRounderPhp}
            onSetPsychOnCall={handleSetPsychOnCall}
            onSetMedOnCall={handleSetMedOnCall}
          />

          {/* Paste census */}
          {!day && (
            <div
              className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
              data-testid="paste-census-panel"
            >
              <div className="mb-4">
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
                  3. Paste admissions list
                </p>
                <h3 className="font-heading text-lg font-medium mt-1">
                  Current inpatient census
                </h3>
                <p className="text-xs text-[#475569] mt-1 flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" />
                  Auto-detects TSV, CSV, or pipe. Recognized columns: Patient, Admission, Unit, Status, Room, Provider. Unit auto-derives from Room prefix (305 → 300).
                </p>
              </div>
              <PasteCensus value={censusText} onChange={setCensusText} />

              {previousDate && (
                <div className="mt-4 flex items-center gap-2 text-sm text-[#475569]">
                  <RefreshCw className="w-4 h-4" />
                  Continuity will be seeded from{" "}
                  <span className="font-medium text-[#0f172a]">{previousDate}</span>
                </div>
              )}

              <div className="mt-6 flex items-center justify-end">
                <Button
                  onClick={handleGenerate}
                  disabled={loading || availableIds.size === 0 || !censusText.trim()}
                  className="bg-[#0284c7] hover:bg-[#21473c] text-white font-medium"
                  data-testid="generate-assignments-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {loading ? "Generating…" : "Generate assignments"}
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Rounding board */}
        {day && (
          <section data-testid="rounding-board-section">
            <div className="mb-6">
              <ContinuityDiff date={currentDate} />
            </div>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4 no-print">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
                  Rounding list
                </p>
                <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1">
                  Assignments by provider
                </h2>
                <p className="text-xs text-[#475569] mt-1">
                  Drag the <span className="font-mono">⋮⋮</span> handle to move a patient between providers, or use the menu (⋯).
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
                  onClick={handleCopyForward}
                  data-testid="build-next-day-button"
                >
                  <CalendarDays className="w-4 h-4 mr-2" /> Build next day from this
                </Button>
              </div>
            </div>

            <RoundingBoard
              day={day}
              providers={providers}
              onChange={async () => {
                const d = await Days.get(currentDate);
                setDay(d);
                loadCompliance(currentDate);
              }}
              date={currentDate}
              compliance={compliance}
            />

            <MedicalRounderPanel
              day={day}
              date={currentDate}
              providerName={providerMap[day.medical_rounder_id]?.name}
            />

            {/* Add census to an existing day */}
            <div className="mt-8 bg-white border border-[#e2e8f0] rounded-xl p-6 no-print" data-testid="append-census-panel">
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
                Update
              </p>
              <h3 className="font-heading text-lg font-medium mt-1">
                Re-run with a new census
              </h3>
              <p className="text-xs text-[#475569] mt-1">
                Paste the updated admissions list to regenerate assignments while preserving
                continuity from this day.
              </p>
              <div className="mt-4">
                <PasteCensus value={censusText} onChange={setCensusText} />
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleGenerate}
                  disabled={loading || availableIds.size === 0 || !censusText.trim()}
                  className="bg-[#0284c7] hover:bg-[#21473c] text-white"
                  data-testid="regenerate-button"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
